import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { FadeIn } from '../ui/FadeIn';
import { CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export function SolutionSection() {
  const features = ['+140 ejercicios analizados', 'Checklists de ejecución', 'Ilustraciones anatómicas', 'Con 5 bonos incluidos'];
  const images = [{
    src: 'https://cdn.magicpatterns.com/uploads/12dfvJBiBooEECfefJRVyG/heylloyd-image-1772660592396.png',
    alt: 'Análisis técnico por ejercicio',
    label: 'Análisis técnico por ejercicio'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/oK4LNtxptYf9gUiWwaN3Ad/heylloyd-image-1772660520858.png',
    alt: 'Ilustraciones anatómicas reales',
    label: 'Ilustraciones anatómicas reales'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/2LBbvNnt9k9k9FpNLmvZJi/heylloyd-image-1772660266126.png',
    alt: 'Base científica aplicada',
    label: 'Base científica aplicada'
  }];
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const goTo = useCallback((index: number) => {
    setCurrent((index + images.length) % images.length);
  }, [images.length]);
  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);
  // Auto-advance every 4 seconds
  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) next();else prev();
    }
  };
  return <section className="py-14 bg-card-dark" data-id="element-357">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-358">
        <FadeIn className="text-center mb-10" data-id="element-359">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white font-heading mb-2" data-id="element-360">
            Esto es lo que tenés disponible
          </h2>
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-black text-primary font-heading mb-4" data-id="element-361">
            antes de tu próxima clase.
          </h3>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-362">
            La próxima vez que un alumno te pregunte por qué ese ejercicio — vas
            a tener la respuesta exacta. Sin dudar. Sin improvisar. Con criterio
            profesional real.
          </p>
        </FadeIn>

        <FadeIn delay={0.3} data-id="element-363">
          <div className="relative max-w-lg mx-auto" data-id="element-364">
            {/* Carousel container */}
            <div className="overflow-hidden rounded-2xl bg-background-dark border border-card-border-dark shadow-xl" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-id="element-365">
              {/* A4 aspect ratio: 1:1.41 */}
              <div className="relative" style={{
              paddingBottom: '141.4%'
            }} data-id="element-366">
                {images.map((img, index) => {
                const isActive = index === current;
                const isNext = index === (current + 1) % images.length;
                // Only render current and next slide (preload next for smooth transition)
                if (!isActive && !isNext) return <div key={index} className="absolute inset-0 opacity-0 pointer-events-none" data-id="element-367" />;
                return <div key={index} className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-368">
                      <img src={img.src} alt={img.alt} className="w-full h-full object-contain p-4" width={400} height={566} loading="lazy" decoding="async" data-id="element-369" />
                    </div>;
              })}
              </div>

              {/* Label overlay */}
              <div className="px-4 pb-4 pt-2 flex items-center gap-3 bg-background-dark" data-id="element-370">
                <CheckCircle2Icon className="w-5 h-5 text-success shrink-0" data-id="element-371" />
                <p className="text-white font-bold text-sm md:text-base" data-id="element-372">
                  {images[current].label}
                </p>
              </div>
            </div>

            {/* Arrow buttons */}
            <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 border border-white/10 flex items-center justify-center text-white hover:bg-black/80 transition-colors z-10" aria-label="Anterior" data-id="element-373">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-374" />
            </button>
            <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 border border-white/10 flex items-center justify-center text-white hover:bg-black/80 transition-colors z-10" aria-label="Siguiente" data-id="element-375">
              <ChevronRightIcon className="w-5 h-5" data-id="element-376" />
            </button>
          </div>

          {/* Dots navigation */}
          <div className="flex justify-center gap-2 mt-4" data-id="element-377">
            {images.map((_, index) => <button key={index} onClick={() => goTo(index)} className={`w-3 h-3 rounded-full transition-all duration-300 ${index === current ? 'bg-primary w-6' : 'bg-gray-600 hover:bg-gray-400'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-378" />)}
          </div>
        </FadeIn>

        <FadeIn delay={0.6} className="mt-10" data-id="element-379">
          <div className="bg-background-dark rounded-2xl p-6 border border-card-border-dark" data-id="element-380">
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8" data-id="element-381">
              {features.map((feature, index) => <div key={index} className="flex items-center gap-2" data-id="element-382">
                  <CheckCircle2Icon className="w-5 h-5 text-success shrink-0" data-id="element-383" />
                  <span className="text-white font-medium text-sm md:text-base" data-id="element-384">
                    {feature}
                  </span>
                </div>)}
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}